import { FacilityFieldsFragment, BffFacility } from '@/graphql/__generated/sdk';

import { FacilityDetailsPageContentData } from '@shared-components/services/getFacilityDetailsPageContentDataByFacilityId';
import { BrandThemeConfig } from '@shared-components/types';
import { getOdpUrl } from '@utils/generateODPLinks';

type OfficeAddress = FacilityFieldsFragment['address'] | BffFacility['address'];

type JumpLink = {
	name?: string | null;
	link?: string | null;
};

type LovetODPLink = {
	id: number;
	path: string;
	title: string;
};

const DEFAULT_LOVET_LINKS = (odpUrl: string): LovetODPLink[] => [
	{ id: 1, path: `${odpUrl}#location-services`, title: 'Location Services' },
	{ id: 2, path: `${odpUrl}#meet-our-doctors`, title: 'Doctors' },
];

const isJumpLink = (value: unknown): value is JumpLink =>
	typeof value === 'object' && value !== null && ('name' in value || 'link' in value);

const extractJumpLinks = (facilityDetailsPage: FacilityDetailsPageContentData | null | undefined): JumpLink[] => {
	const json = facilityDetailsPage?.pageContentDataJson as { jumpLinks?: unknown } | null | undefined;
	const jumpLinks = json?.jumpLinks;

	if (!Array.isArray(jumpLinks)) {
		return [];
	}

	return jumpLinks.filter(isJumpLink);
};

export function generateLovetODPLinks(
	address: OfficeAddress,
	config: BrandThemeConfig,
	facilityDetailsPage?: FacilityDetailsPageContentData | null
): LovetODPLink[] {
	const odpUrl = getOdpUrl(address, config) || '';

	const jumpLinks = extractJumpLinks(facilityDetailsPage);

	if (jumpLinks.length === 0) {
		return DEFAULT_LOVET_LINKS(odpUrl);
	}

	return jumpLinks
		.map((jumpLink, index) => {
			const title = jumpLink.name?.trim();
			const link = jumpLink.link?.trim();

			if (!title || !link) {
				return null;
			}

			const path = link.startsWith('#') ? `${odpUrl}${link}` : link;

			return { id: index + 1, path, title };
		})
		.filter((link): link is LovetODPLink => link !== null);
}
